import React from 'react';
import {
  IAurReq,
  IClientReq,
  IRegulatoryReq
} from 'store/ducks/ClientReq/types';
import { CategoryList } from 'store/ducks/globalTypes';

// Single reusable function that consolidates the most duplicated pattern
export const useDataProcessor = () => {
  // Normalize AUR requirements (used in multiple places)
  const normalizeAurReqs = (aurReq: any): IAurReq[] => {
    if (!aurReq) return [];
    return aurReq.id ? [aurReq] : Object.values(aurReq);
  };

  // Get category from requirement (used in multiple places)
  const getCategoryFromReq = (req: IClientReq): CategoryList | undefined => {
    if ((req as IClientReq & { category_id?: CategoryList }).category_id)
      return (req as IClientReq & { category_id?: CategoryList }).category_id;
    if ((req as IClientReq & { category?: CategoryList }).category)
      return (req as IClientReq & { category?: CategoryList }).category;
    if ((req as IClientReq & { cat?: CategoryList }).cat)
      return (req as IClientReq & { cat?: CategoryList }).cat;
    return undefined;
  };

  // Process data with filters (consolidates filterRegReqs + getFilteredDataSource logic)
  const processFilteredData = (
    baseData: IRegulatoryReq[],
    filters: {
      applicability?: string[];
      searchText?: string;
      aurSearchText?: string;
      clientSearchText?: string;
      categoryFilter?: CategoryList | 'all';
    },
    checkedAurReqs: number[]
  ): IRegulatoryReq[] => {
    if (!baseData || baseData.length === 0) return [];

    let filteredData = [...baseData];

    // Apply applicability filter
    const currentFilter = filters.applicability?.[0];
    if (currentFilter && currentFilter !== 'all') {
      const newRows = baseData.filter(
        (regReq) => regReq.isTemporary || regReq.isNewRow
      );

      let applicabilityFiltered: IRegulatoryReq[] = [];
      if (currentFilter === 'Y') {
        applicabilityFiltered = baseData.filter((regReq) => {
          if (!regReq.AUR_REQ) return false;
          const aurReqs = normalizeAurReqs(regReq.AUR_REQ);
          return aurReqs.some((aurReq) => checkedAurReqs.includes(aurReq.id));
        });
      } else if (currentFilter === 'N') {
        applicabilityFiltered = baseData.filter((regReq) => {
          if (!regReq.AUR_REQ) return true;
          const aurReqs = normalizeAurReqs(regReq.AUR_REQ);
          return aurReqs.every((aurReq) => !checkedAurReqs.includes(aurReq.id));
        });
      }

      filteredData = [...applicabilityFiltered, ...newRows];
    }

    // Apply search filters
    if (filters.searchText?.trim()) {
      filteredData = filteredData.filter((record) =>
        record.value?.toLowerCase().includes(filters.searchText!.toLowerCase())
      );
    }

    if (filters.aurSearchText?.trim()) {
      filteredData = filteredData.filter((record) => {
        if (!record.AUR_REQ) return false;
        const aurReqs = normalizeAurReqs(record.AUR_REQ);
        return aurReqs.some((aurReq) =>
          aurReq.value
            ?.toLowerCase()
            .includes(filters.aurSearchText!.toLowerCase())
        );
      });
    }

    if (filters.clientSearchText?.trim()) {
      filteredData = filteredData.filter((record) => {
        if (!record.AUR_REQ) return false;
        const aurReqs = normalizeAurReqs(record.AUR_REQ);
        return aurReqs.some((aurReq) => {
          if (!aurReq.CLN_REQ) return false;
          const clnReqs = Object.values(aurReq.CLN_REQ);
          return clnReqs.some((clnReq: IClientReq) =>
            clnReq.value
              ?.toLowerCase()
              .includes(filters.clientSearchText!.toLowerCase())
          );
        });
      });
    }

    // Apply category filter
    if (filters.categoryFilter && filters.categoryFilter !== 'all') {
      filteredData = filteredData
        .map((record) => {
          if (!record.AUR_REQ) return null;

          const aurReqs = normalizeAurReqs(record.AUR_REQ);

          // Filter AUR requirements to only show those matching the category
          const matchingAurReqs = aurReqs.filter((aurReq) => {
            if (aurReq.category_id === filters.categoryFilter) {
              return true;
            }

            if (aurReq.CLN_REQ) {
              return Object.values(aurReq.CLN_REQ).some((clnReq) => {
                const category = getCategoryFromReq(clnReq as IClientReq);
                return category === filters.categoryFilter;
              });
            }

            return false;
          });

          if (matchingAurReqs.length === 0) return null;

          return {
            ...record,
            AUR_REQ:
              matchingAurReqs.length === 1
                ? matchingAurReqs[0]
                : matchingAurReqs
          };
        })
        .filter((record) => record !== null) as IRegulatoryReq[];
    }

    return filteredData;
  };

  // Process applied filters result (used in multiple places)
  const processAppliedFiltersResult = (
    appliedFiltersResult: IRegulatoryReq[],
    processor: (regReq: IRegulatoryReq, aurReq: IAurReq) => void
  ) => {
    if (!appliedFiltersResult) return;

    appliedFiltersResult.forEach((regReq: IRegulatoryReq) => {
      if (regReq.AUR_REQ) {
        const aurReqs = normalizeAurReqs(regReq.AUR_REQ);
        aurReqs.forEach((aurReq: IAurReq) => processor(regReq, aurReq));
      }
    });
  };

  // Reusable click outside handler (consolidates two identical useEffect patterns)
  const useClickOutside = (
    ref: React.RefObject<HTMLElement>,
    isOpen: boolean,
    onClose: () => void,
    dropdownClass?: string
  ) => {
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (ref.current && !ref.current.contains(target)) {
          if (dropdownClass) {
            const dropdown = document.querySelector(`.${dropdownClass}`);
            if (!dropdown || !dropdown.contains(target)) {
              onClose();
            }
          } else {
            onClose();
          }
        }
      };

      if (isOpen) {
        setTimeout(() => {
          document.addEventListener('mousedown', handleClickOutside);
        }, 100);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, ref, onClose, dropdownClass]);
  };

  // Find category from applied filters
  const findCategoryFromAppliedFilters = (
    appliedFiltersResult: IRegulatoryReq[],
    targetId: string
  ): CategoryList | undefined => {
    if (!appliedFiltersResult || !targetId) return undefined;

    let foundCategory: CategoryList | undefined;

    processAppliedFiltersResult(
      appliedFiltersResult,
      (regReq: IRegulatoryReq, aurReq: IAurReq) => {
        if (foundCategory !== undefined) return; // Early exit if already found

        if (aurReq.CLN_REQ) {
          for (const clnReq of Object.values(aurReq.CLN_REQ)) {
            const clnReqTyped = clnReq as IClientReq;
            if (
              clnReqTyped &&
              (clnReqTyped.id?.toString() === targetId ||
                clnReqTyped.bru_id?.toString() === targetId)
            ) {
              foundCategory = getCategoryFromReq(clnReqTyped);
              break;
            }
          }
        }
      }
    );

    return foundCategory;
  };

  return {
    normalizeAurReqs,
    processFilteredData,
    getCategoryFromReq,
    processAppliedFiltersResult,
    useClickOutside,
    findCategoryFromAppliedFilters
  };
};

export default useDataProcessor;
